import { LayerEffectStyle, TextStyle } from "../api/model";
import ComponentProperty from "../api/model/components/ComponentProperty";
import ApiBlur from "../api/model/snapshots/layers/Blur";
import Constraints from "../api/model/snapshots/layers/Constraints";
import { LayerType, LAYER_TYPES, GridChildAlignment } from "../api/model/snapshots/layers/Layer";
import ApiLayout, { LayoutAlignment } from "../api/model/snapshots/layers/Layout";
import Rect from "../api/model/snapshots/layers/Rect";
import ZeplinBlendMode from "../api/model/snapshots/layers/ZeplinBlendMode";
import { VariableReference } from "../api/model/variables/Variables";

import { Modify } from "../utils/UtilityTypes";

import Asset, { AssetContent } from "./Asset";

import Blur from "./blur";
import Border from "./border";
import { BLEND_MODES } from "./constants";
import CornerRadius from "./CornerRadius";
import Fill from "./fill";
import Font from "./font";
import InspectLayer from "./InspectLayer";
import Layout from "./Layout";
import Model from "./model";
import Shadow from "./shadow";

type LayerTextStyle = {
    range: {
        start: number;
        end: number;
    };
    font: Font;
    hyperlink?: string;
};

type LayerObject = Modify<Omit<InspectLayer, "_id" | "parent">, {
    layers: LayerObject[];
}> & {
    assets: Pick<AssetContent, "densityScale" | "format">[];
};

export default class Layer extends Model {
    id: string;
    _id: string;
    sourceId: string | undefined;
    #type: LayerType;
    name: string;
    rect: Rect;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    absoluteRect: Rect;
    absoluteRenderRect?: Rect;
    fills: Fill[];
    borders: Border[];
    shadows: Shadow[];
    #blur?: Blur;
    #layout?: Layout;
    rotation: number | undefined;
    #borderRadius: number;
    cornerRadius?: CornerRadius;
    textStyles: LayerTextStyle[];
    #content: string | undefined;
    #opacity: number;
    #blendMode: ZeplinBlendMode | undefined;
    exportable: boolean | undefined;
    componentName: string | undefined;
    componentId: string | undefined;
    componentProperties?: ComponentProperty[];
    interactionLevel?: string;
    layers: Layer[];
    constraints?: Constraints;
    assets: Pick<AssetContent, "densityScale" | "format">[];
    assetData: Asset | undefined;
    styleName: string | undefined;
    textStyleName: string | undefined;
    layoutGrow: number | undefined;
    layoutAlignment: LayoutAlignment | undefined;
    textStyleSourceId: unknown | undefined;
    parent: InspectLayer | null | undefined;
    fromLibrary: boolean | undefined;
    libraryName: string | undefined;
    variableReferences: VariableReference[] | undefined;
    fillStyle?: LayerEffectStyle;
    borderStyle?: LayerEffectStyle;
    effectStyle?: LayerEffectStyle;

    // Figma grid
    gridChildHorizontalAlignment?: GridChildAlignment;
    gridChildVerticalAlignment?: GridChildAlignment;
    gridColumnIndex?: number;
    gridColumnSpan?: number;
    gridRowIndex?: number;
    gridRowSpan?: number;

    constructor({
        blur,
        layout,
        borders,
        fills,
        shadows,
        textStyles,
        content,
        textStyleSourceId,
        layers,
        cornerRadius,
        asset,
        _id,
        type,
        name,
        exportable,
        rotation,
        opacity,
        blendMode,
        borderRadius,
        absoluteRect,
        absoluteRenderRect,
        rect,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        componentName,
        componentId,
        componentProperties,
        sourceId,
        layoutGrow,
        layoutAlignment,
        styleName,
        textStyleName,
        interactionLevel,
        parent,
        constraints,
        fromLibrary,
        libraryName,
        variableReferences,
        fillStyle,
        borderStyle,
        effectStyle,
        gridChildHorizontalAlignment,
        gridChildVerticalAlignment,
        gridColumnIndex,
        gridColumnSpan,
        gridRowIndex,
        gridRowSpan
    }: InspectLayer & { asset?: Asset; }) {
        super();

        if (blur) {
            this.#blur = new Blur(blur);
        }

        if (layout) {
            this.#layout = new Layout(layout);
        }

        this.borders = borders.map(border => new Border(border));
        this.fills = fills.map(fill => new Fill(fill));
        this.shadows = shadows.map(shadow => new Shadow(shadow));

        this.textStyles = textStyles?.map(fontWithRange => {
            /* @ts-ignore */ /** {@link parseInt} allows number parameters, its type definition is wrong */
            const start = parseInt(fontWithRange.range.location, 10);
            /* @ts-ignore */ /** {@link parseInt} allows number parameters, its type definition is wrong */
            let length = parseInt(fontWithRange.range.length, 10);

            if (textStyles.length === 1 && length === 0 && content) {
                // Temporary fix for 0-length text style
                length = content.length;
            }

            const { hyperlink, ...style } = fontWithRange.style;

            const font = new Font({
                sourceId: textStyleSourceId,
                ...style
            });

            return {
                range: {
                    start,
                    end: start + length
                },
                font,
                hyperlink
            };
        }) ?? [];

        this.layers = layers?.map(layer => new Layer(layer)) ?? [];

        if (cornerRadius) {
            this.cornerRadius = new CornerRadius(cornerRadius);
        }

        this.assets = asset?.contents.map(({ densityScale, format }) => ({ densityScale, format })) ?? [];
        this.id = _id;
        this._id = _id;
        this.#type = type;
        this.name = name;
        this.exportable = exportable;
        this.rotation = rotation;
        this.#opacity = opacity;
        this.#blendMode = blendMode;
        this.#borderRadius = borderRadius;
        this.absoluteRect = absoluteRect;
        this.absoluteRenderRect = absoluteRenderRect;
        this.rect = rect;
        this.minWidth = minWidth;
        this.maxWidth = maxWidth;
        this.minHeight = minHeight;
        this.maxHeight = maxHeight;
        this.#content = content;
        this.assetData = asset;
        this.componentName = componentName;
        this.componentId = componentId;
        this.componentProperties = componentProperties;
        this.textStyleSourceId = textStyleSourceId;
        this.sourceId = sourceId;
        this.layoutGrow = layoutGrow;
        this.layoutAlignment = layoutAlignment;
        this.styleName = styleName;
        this.textStyleName = textStyleName;
        this.interactionLevel = interactionLevel;
        this.parent = parent;
        this.constraints = constraints;
        this.fromLibrary = fromLibrary;
        this.libraryName = libraryName;
        this.variableReferences = variableReferences;
        this.fillStyle = fillStyle;
        this.borderStyle = borderStyle;
        this.effectStyle = effectStyle;

        // Figma grid
        this.gridChildHorizontalAlignment = gridChildHorizontalAlignment;
        this.gridChildVerticalAlignment = gridChildVerticalAlignment;
        this.gridColumnIndex = gridColumnIndex;
        this.gridColumnSpan = gridColumnSpan;
        this.gridRowIndex = gridRowIndex;
        this.gridRowSpan = gridRowSpan;
    }

    get type(): LayerType {
        return this.#type;
    }

    set type(type: LayerType) {
        if (!LAYER_TYPES.includes(type)) {
            throw new TypeError(`Unknown layer type: '${type}'. Possible values: ${JSON.stringify(LAYER_TYPES)}`);
        }

        this.#type = type;
    }

    get opacity(): number {
        return this.#opacity;
    }

    set opacity(opacity: number) {
        if (opacity < 0 || opacity > 1) {
            throw new RangeError(`Invalid opacity value: '${opacity}'. Valid range: [0, 1]`);
        }

        this.#opacity = opacity;
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get blendMode(): ZeplinBlendMode | undefined {
        return this.#blendMode;
    }

    set blendMode(blendMode: ZeplinBlendMode) {
        if (!BLEND_MODES.includes(blendMode)) {
            throw new TypeError(`Unknown blend mode: '${blendMode}'. Possible values: ${JSON.stringify(BLEND_MODES)}`);
        }

        this.#blendMode = blendMode;
    }

    get borderRadius(): number {
        return this.#borderRadius;
    }

    set borderRadius(borderRadius: number) {
        if (borderRadius < 0) {
            throw new RangeError(`Invalid radius value: '${borderRadius}'. Valid range: [0, ∞)`);
        }

        this.#borderRadius = borderRadius;
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get blur(): Blur | undefined {
        return this.#blur;
    }

    set blur(blur: ApiBlur) {
        this.#blur = new Blur(blur);
    }

    get content(): string {
        return this.type === "text" ? this.#content ?? "" : "";
    }

    set content(content: string) {
        if (this.#type === "text") {
            this.#content = content;
        }
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get layout(): Layout | undefined {
        return this.#layout;
    }

    set layout(layout: ApiLayout) {
        this.#layout = new Layout(layout);
    }

    toJSON(key: string): string | Layer {
        if (key === "parent") {
            return "[Parent]";
        }

        return this;
    }

    override toObject(): LayerObject {
        const layerObj: LayerObject = {
            type: this.type,
            name: this.name,
            exportable: this.exportable,
            rotation: this.rotation ?? 0,
            opacity: this.opacity,
            blendMode: this.blendMode ?? "normal",
            borderRadius: this.borderRadius ?? 0,
            absoluteRect: this.absoluteRect,
            absoluteRenderRect: this.absoluteRenderRect,
            rect: this.rect,
            content: this.content,
            borders: this.borders.map(border => border.toObject()),
            fills: this.fills.map(fill => fill.toObject()),
            shadows: this.shadows.map(shadow => shadow.toObject()),
            textStyles: this.textStyles.map(textStyleWithRange => {
                const { range } = textStyleWithRange;

                return {
                    range: {
                        location: range.start,
                        length: range.end - range.start
                    },
                    style: textStyleWithRange.font.toObject() as TextStyle
                };
            }),
            assets: this.assets,
            layers: this.layers.map(layer => layer.toObject()),
            componentName: this.componentName,
            componentId: this.componentId,
            componentProperties: this.componentProperties,
            styleName: this.styleName,
            textStyleName: this.textStyleName,
            sourceId: this.sourceId,
            interactionLevel: this.interactionLevel,
            layoutGrow: this.layoutGrow,
            layoutAlignment: this.layoutAlignment,
            constraints: this.constraints,
            cornerRadius: this.cornerRadius?.toObject(),
            fromLibrary: this.fromLibrary,
            libraryName: this.libraryName,
            variableReferences: this.variableReferences,
            gridChildHorizontalAlignment: this.gridChildHorizontalAlignment,
            gridChildVerticalAlignment: this.gridChildVerticalAlignment,
            gridColumnIndex: this.gridColumnIndex,
            gridColumnSpan: this.gridColumnSpan,
            gridRowIndex: this.gridRowIndex,
            gridRowSpan: this.gridRowSpan
        };

        if (this.#blur) {
            layerObj.blur = this.#blur.toObject();
        }

        if (this.#layout) {
            layerObj.layout = this.#layout.toObject();
        }

        return layerObj;
    }

    hasGradient(): boolean {
        return this.fills.some(fill => fill.type === "gradient");
    }

    hasBlendMode(): boolean {
        return this.fills.some(fill => fill.blendMode && fill.blendMode !== "normal");
    }

    getDefaultTextStyle(): Font | undefined {
        if (this.type !== "text" || !this.textStyles.length) {
            return;
        }

        type TextStyleGroup = {
            font: Font;
            totalLength: number;
        };
        const groupedTextStyles: TextStyleGroup[] = [];
        let maxLength = 0;
        let defaultTextStyle: TextStyleGroup | undefined;

        this.textStyles.forEach(textStyleWithRange => {
            let textStyleGroup = groupedTextStyles.find(({ font }) => font.equals(textStyleWithRange.font));
            const length = textStyleWithRange.range.end - textStyleWithRange.range.start;

            if (!textStyleGroup) {
                textStyleGroup = {
                    font: textStyleWithRange.font,
                    totalLength: 0
                };
                groupedTextStyles.push(textStyleGroup);
            }

            textStyleGroup.totalLength += length;
            if (textStyleGroup.totalLength > maxLength) {
                maxLength = textStyleGroup.totalLength;
                defaultTextStyle = textStyleGroup;
            }
        });

        return defaultTextStyle?.font;
    }
}
